import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGenerateMediaPreview,
  useGetMediaPreview,
} from "~/api/custom-reports/custom-reports";
import { MediaPreviewResultStatus } from "~/api/model";
import logger from "~/utils/logger";

const MEDIA_PREVIEW_POLL_INTERVAL_MS = 3000;
const MEDIA_PREVIEW_TIMEOUT_MS = 120_000;

type MediaPreviewStatus = "idle" | "generating" | "ready" | "error";

export interface UseFtueMediaPreviewResult {
  content: string | null;
  status: MediaPreviewStatus;
  error: string | null;
  generate: () => void;
  cancel: () => void;
}

export const useFtueMediaPreview = (
  organizationId: string,
  reportSampleSource: string | null
): UseFtueMediaPreviewResult => {
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    mutate: generateMediaPreview,
    isPending: isStartingPreview,
    isError: isStartError,
    reset: resetGenerate,
  } = useGenerateMediaPreview();
  const generateMediaPreviewRef = useRef(generateMediaPreview);
  generateMediaPreviewRef.current = generateMediaPreview;
  const resetGenerateRef = useRef(resetGenerate);
  resetGenerateRef.current = resetGenerate;

  const isPollingEnabled =
    !!organizationId && !!previewId && !isCancelled && !isTimedOut;

  const { data: previewData, isError: isPreviewQueryError } =
    useGetMediaPreview(organizationId, previewId ?? "", {
      query: {
        enabled: isPollingEnabled,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return MEDIA_PREVIEW_POLL_INTERVAL_MS;

          const terminal =
            data.status === MediaPreviewResultStatus.completed ||
            data.status === MediaPreviewResultStatus.failed;

          return terminal ? false : MEDIA_PREVIEW_POLL_INTERVAL_MS;
        },
        refetchIntervalInBackground: true,
      },
    });

  const isPreviewComplete =
    previewData?.status === MediaPreviewResultStatus.completed;
  const isPreviewFailed =
    previewData?.status === MediaPreviewResultStatus.failed;

  // Timeout guard: arm when polling begins, clear when terminal/cancelled
  useEffect(() => {
    if (!isPollingEnabled || isPreviewComplete || isPreviewFailed) return;

    timeoutRef.current = setTimeout(() => {
      setIsTimedOut(true);
      logger.error("Media preview generation timed out");
    }, MEDIA_PREVIEW_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isPollingEnabled, isPreviewComplete, isPreviewFailed]);

  const deriveStatus = (): MediaPreviewStatus => {
    if (isCancelled && !previewId && !isStartingPreview) return "idle";
    if (isTimedOut) return "error";
    if (startError) return "error";
    if (isStartError) return "error";
    if (isPreviewQueryError) return "error";
    if (isPreviewFailed) return "error";
    if (isPreviewComplete) return "ready";
    if (isStartingPreview || previewId) return "generating";
    return "idle";
  };

  const status = deriveStatus();
  const statusRef = useRef<MediaPreviewStatus>(status);
  statusRef.current = status;

  const content = isPreviewComplete ? (previewData?.content ?? "") : null;

  const deriveError = (): string | null => {
    if (isTimedOut) return "Media preview generation timed out";
    if (startError) return startError;
    if (isStartError) return "Failed to start media preview";
    if (isPreviewFailed)
      return previewData?.error ?? "Media preview generation failed";
    if (isPreviewQueryError) return "Failed to poll media preview status";
    return null;
  };

  const error = deriveError();

  const generate = useCallback(() => {
    if (!reportSampleSource || !organizationId) return;
    if (statusRef.current === "generating" || statusRef.current === "ready")
      return;

    setIsCancelled(false);
    setIsTimedOut(false);
    setStartError(null);
    setPreviewId(null);

    generateMediaPreviewRef.current(
      {
        organizationId,
        data: { reportSampleSource },
      },
      {
        onSuccess: (response) => {
          setPreviewId(response.previewId);
        },
        onError: (err) => {
          logger.error("Failed to start media preview", err);
        },
      }
    );
  }, [organizationId, reportSampleSource]);

  const cancel = useCallback(() => {
    setIsCancelled(true);
    setPreviewId(null);
    setStartError(null);
    resetGenerateRef.current();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return { content, status, error, generate, cancel };
};
